import { useContext } from 'react';
import { MultiPortfolioReviewContext } from '../components/common/MultiPortfolioReviewContext';
import { MultiHistoricalPortfolioContext } from '../components/specialized/MultiHistoricalPortfolioContext';
import type { Portfolio } from 'venn-api';
import { compact, isNil, memoize } from 'lodash';
import { recalculateAssets } from '../review/helpers';

export const useMultiHistoricalRightPaneContentPortfolio = () => {
  const {
    data: { parsedResults, selectedIndex },
  } = useContext(MultiPortfolioReviewContext);
  const { selectedDate } = useContext(MultiHistoricalPortfolioContext);
  const selectedPortfolio = parsedResults[selectedIndex]!.parsedPortfolio;
  const excludedInvestments = parsedResults[selectedIndex]!.excludedInvestments;

  // filter to selected closing date allocations
  const timestamp = selectedDate.valueOf();
  const filteredPortfolio = filterClosingAllocations(selectedPortfolio, timestamp);

  // remap allocations
  let completePortfolio = remapAllocations(selectedPortfolio);
  let selectedDateFilteredPortfolio = remapAllocations(filteredPortfolio);

  // recalculate allocations
  completePortfolio = recalculateAssets(completePortfolio, excludedInvestments);
  selectedDateFilteredPortfolio = recalculateAssets(selectedDateFilteredPortfolio, excludedInvestments);

  return {
    selectedDateFilteredPortfolio,
    completePortfolio,
  };
};

const filterClosingAllocations = (portfolio: Portfolio, timestamp: number): Portfolio => {
  return {
    ...portfolio,
    closingAllocationsTs: compact([
      (portfolio.closingAllocationsTs ?? ([] as number[][]))
        .filter((a) => !isNil(a))
        .filter((a) => a[0]! <= timestamp)
        .sort((a, b) => b[0]! - a[0]!)?.[0],
    ]),
    children: portfolio.children.map((childPortfolio) => filterClosingAllocations(childPortfolio, timestamp)),
  };
};

const remapAllocations = (portfolio: Portfolio): Portfolio => {
  const allocation = sumAllocation(portfolio);
  return {
    ...portfolio,
    allocation,
    netAssets: allocation,
    children: portfolio.children.map((childPortfolio) => remapAllocations(childPortfolio)),
  };
};
const sumAllocation = (portfolio: Portfolio): number => {
  const _sumAllocation = memoize((portfolio: Portfolio): number => {
    const fundAllocation = portfolio.closingAllocationsTs?.filter((a) => !isNil(a)).sort((a, b) => b[0]! - a[0]!)?.[0];
    const fundAllocationValue = fundAllocation ? fundAllocation[1]! : 0;
    return (
      fundAllocationValue + portfolio.children.reduce((acc, childPortfolio) => acc + _sumAllocation(childPortfolio), 0)
    );
  });
  const result = _sumAllocation(portfolio);
  _sumAllocation.cache.clear?.();
  return result;
};
